import { useCallback, useEffect, useRef, useState } from 'react';
import { errorMessage, type JsonMap } from '@/api/client';

export type AsyncState<T> = {
  loading: boolean;
  error: string;
  data: T | null;
  reload: () => void;
};

export function useAsync<T>(
  loader: () => Promise<JsonMap>,
  map: (payload: JsonMap) => T,
  deps: unknown[] = [],
): AsyncState<T> {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState<T | null>(null);
  const [tick, setTick] = useState(0);
  const hasDataRef = useRef(false);

  const reload = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    // Avoid full-page flash on polling refreshes when data is already shown.
    if (!hasDataRef.current) setLoading(true);
    setError('');
    void loader()
      .then((payload) => {
        if (cancelled) return;
        if (!payload.success) {
          setError(errorMessage(payload));
          if (!hasDataRef.current) setData(null);
          return;
        }
        setData(map(payload));
        hasDataRef.current = true;
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'تعذر الاتصال بالخادم.');
        if (!hasDataRef.current) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, ...deps]);

  return { loading, error, data, reload };
}
